import { z } from 'zod';
import { uuidSchema } from './common.schema';

// ---------------------------------------------------------------------------
// Status / enum constants
// ---------------------------------------------------------------------------

export const COMMUNICATION_TYPES = [
  'RECALL_NOTICE',
  'CLINICAL_UPDATE',
  'CONSENT_REQUEST',
  'DISCHARGE_SUMMARY',
  'GENERAL',
] as const;
export type CommunicationType = (typeof COMMUNICATION_TYPES)[number];

export const COMMUNICATION_CHANNELS = [
  'IN_APP',
  'PHONE',
  'IN_PERSON',
  'EMAIL',
  'SMS',
  'WRITTEN',
] as const;
export type CommunicationChannel = (typeof COMMUNICATION_CHANNELS)[number];

export const RECIPIENT_TYPES = [
  'PARENT',
  'GUARDIAN',
  'NEXT_OF_KIN',
  'CLINICAL_STAFF',
] as const;
export type RecipientType = (typeof RECIPIENT_TYPES)[number];

export const COMMUNICATION_STATUSES = [
  'PENDING',
  'SENT',
  'DELIVERED',
  'ACKNOWLEDGED',
  'FAILED',
  'NO_RESPONSE',
] as const;
export type CommunicationStatus = (typeof COMMUNICATION_STATUSES)[number];

// ---------------------------------------------------------------------------
// POST — create
// ---------------------------------------------------------------------------

export const CreatePatientCommunicationSchema = z.object({
  hospital_id: uuidSchema,
  patient_id: uuidSchema,
  encounter_id: uuidSchema.optional(),
  recall_hospital_id: uuidSchema.optional(),
  communication_type: z.enum(COMMUNICATION_TYPES).default('RECALL_NOTICE'),
  channel: z.enum(COMMUNICATION_CHANNELS),
  recipient_type: z.enum(RECIPIENT_TYPES).default('GUARDIAN'),
  recipient_name: z.string().trim().max(200).optional(),
  recipient_contact: z.string().trim().max(200).optional(),
  subject: z.string().trim().max(300).optional(),
  message_body: z.string().trim().min(1),
  delivered_at: z.string().datetime().optional(),
  initiated_by: uuidSchema,
});

export type CreatePatientCommunication = z.infer<typeof CreatePatientCommunicationSchema>;

// ---------------------------------------------------------------------------
// PUT — update
// ---------------------------------------------------------------------------

export const UpdatePatientCommunicationSchema = z.object({
  // identifiers
  encounter_id: uuidSchema.optional(),
  // content
  communication_type: z.enum(COMMUNICATION_TYPES).optional(),
  channel: z.enum(COMMUNICATION_CHANNELS).optional(),
  recipient_type: z.enum(RECIPIENT_TYPES).optional(),
  recipient_name: z.string().trim().max(200).optional(),
  recipient_contact: z.string().trim().max(200).optional(),
  subject: z.string().trim().max(300).optional(),
  message_body: z.string().trim().min(1).optional(),
  // status & timestamps
  status: z.enum(COMMUNICATION_STATUSES).optional(),
  sent_at: z.string().datetime().optional(),
  delivered_at: z.string().datetime().optional(),
  acknowledged_at: z.string().datetime().optional(),
  // notes
  response_notes: z.string().trim().optional(),
  failure_reason: z.string().trim().max(500).optional(),
}).refine(
  (data) => Object.keys(data).length > 0,
  { message: 'At least one field must be provided for update' },
);

export type UpdatePatientCommunication = z.infer<typeof UpdatePatientCommunicationSchema>;

// ---------------------------------------------------------------------------
// GET list — query params
// ---------------------------------------------------------------------------

export const PatientCommunicationListQuerySchema = z.object({
  recall_hospital_id: uuidSchema.optional(),
  patient_id: uuidSchema.optional(),
  status: z.enum(COMMUNICATION_STATUSES).optional(),
  channel: z.enum(COMMUNICATION_CHANNELS).optional(),
  communication_type: z.enum(COMMUNICATION_TYPES).optional(),
  page: z.coerce.number().int().positive().default(1),
  limit: z.coerce.number().int().min(1).max(100).default(20),
});

export type PatientCommunicationListQuery = z.infer<typeof PatientCommunicationListQuerySchema>;

// ---------------------------------------------------------------------------
// Response item
// ---------------------------------------------------------------------------

export const PatientCommunicationItemSchema = z.object({
  communication_id: uuidSchema,
  hospital_id: uuidSchema,
  patient_id: uuidSchema,
  encounter_id: uuidSchema.nullable(),
  recall_hospital_id: uuidSchema.nullable(),
  communication_type: z.string(),
  channel: z.string(),
  recipient_type: z.string(),
  recipient_name: z.string().nullable(),
  recipient_contact: z.string().nullable(),
  subject: z.string().nullable(),
  message_body: z.string(),
  status: z.string(),
  sent_at: z.string().nullable(),
  delivered_at: z.string().nullable(),
  acknowledged_at: z.string().nullable(),
  response_notes: z.string().nullable(),
  failure_reason: z.string().nullable(),
  initiated_by: uuidSchema,
  created_at: z.string(),
  updated_at: z.string(),
});

export type PatientCommunicationItem = z.infer<typeof PatientCommunicationItemSchema>;

export const PatientCommunicationListResponseSchema = z.object({
  items: z.array(PatientCommunicationItemSchema),
  pagination: z.object({
    currentPage: z.number().int(),
    totalPages: z.number().int(),
    totalItems: z.number().int(),
    itemsPerPage: z.number().int(),
    hasNextPage: z.boolean(),
    hasPreviousPage: z.boolean(),
  }),
});

export type PatientCommunicationListResponse = z.infer<typeof PatientCommunicationListResponseSchema>;
